/**
 * A compact announcement dialog positioned below its announcement row.
 * Ported from the Editors' Pick (Horizon) announcement modal.
 */
if (!customElements.get('announcement-modal')) {
  class AnnouncementModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.announcement-modal__dialog');
      this.trigger = this.querySelector('.js-announcement-modal-open');
      this.closeButton = this.querySelector('.js-announcement-modal-close');
      if (!this.dialog || !this.trigger) return;

      this.previousScrollY = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.showDialog = this.showDialog.bind(this);
      this.closeDialog = this.closeDialog.bind(this);
      this.updatePosition = this.updatePosition.bind(this);

      this.trigger.addEventListener('click', this.showDialog);
      if (this.closeButton) this.closeButton.addEventListener('click', this.closeDialog);
      window.addEventListener('resize', this.updatePosition);
    }

    disconnectedCallback() {
      if (!this.dialog || !this.trigger) return;
      this.trigger.removeEventListener('click', this.showDialog);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.closeDialog);
      window.removeEventListener('resize', this.updatePosition);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeyDown);
    }

    updatePosition() {
      const bar = this.closest('.announcement');
      if (!bar) return;

      // Measure before the body scroll lock moves the page.
      const top = Math.min(Math.max(bar.getBoundingClientRect().bottom + 12, 12), window.innerHeight - 120);
      this.dialog.style.setProperty('--announcement-modal-top', `${Math.max(12, top)}px`);
    }

    showDialog() {
      if (this.dialog.open) return;

      this.updatePosition();
      const scrollY = window.scrollY;
      this.previousScrollY = scrollY;

      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;

        this.dialog.showModal();

        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeyDown);
      });
    }

    closeDialog() {
      if (!this.dialog.open) return;

      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeyDown);

      // Restart the animation so the closing keyframes always play.
      this.dialog.style.animation = 'none';
      void this.dialog.offsetWidth;
      this.dialog.classList.add('dialog-closing');
      this.dialog.style.animation = '';

      const finish = () => {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });

        this.dialog.close();
        this.dialog.classList.remove('dialog-closing');
      };

      const animations = this.dialog.getAnimations ? this.dialog.getAnimations() : [];
      if (animations.length) {
        Promise.all(animations.map((animation) => animation.finished)).then(finish, finish);
      } else {
        finish();
      }
    }

    handleClick(event) {
      const rect = this.dialog.getBoundingClientRect();
      const isOutside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      if (isOutside) this.closeDialog();
    }

    handleKeyDown(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      this.closeDialog();
    }
  }

  customElements.define('announcement-modal', AnnouncementModal);
}
